/**
 * Recently Viewed Projects — pure cache logic.
 * Backs a single `localStorage[CACHE_KEY]` entry holding `[{projectId, viewedAt}]`.
 * See FeatureSpecs/RecentlyViewedProjects/FEATURE_SPEC.md R1-R5, R11, R12.
 */

export const CACHE_KEY = 'recentlyViewedProjects';

const MAX_ENTRIES = 9;
const MAX_AGE_MS = 15 * 24 * 60 * 60 * 1000;

function readEntries() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // ponytail: malformed cache is treated as empty rather than throwing; keeps this a pure read-normalize-write module
    return [];
  }
}

function writeEntries(entries) {
  localStorage.setItem(CACHE_KEY, JSON.stringify(entries));
}

/**
 * Adds/updates a project view: removes any existing entry for the same projectId,
 * prepends a fresh entry with the current ISO timestamp, and caps to MAX_ENTRIES.
 * @param {string} projectId
 */
export function recordProjectView(projectId) {
  const id = String(projectId);
  const entries = readEntries().filter((entry) => String(entry.projectId) !== id);
  entries.unshift({ projectId: id, viewedAt: new Date().toISOString() });
  writeEntries(entries.slice(0, MAX_ENTRIES));
}

/**
 * Normalizes the cache (dedupe by projectId keeping newest viewedAt, sort desc,
 * expire entries older than 15 days, cap to 9), persists the normalized result,
 * and returns the ordered projectId strings (most-recent-first).
 * @returns {string[]}
 */
export function getEligibleProjectIds() {
  const now = Date.now();

  const newestByProjectId = new Map();
  for (const entry of readEntries()) {
    // legacy entries may predate the string-coercion fix in recordProjectView, so normalize here too
    const projectId = String(entry.projectId);
    const existing = newestByProjectId.get(projectId);
    if (!existing || new Date(entry.viewedAt) > new Date(existing.viewedAt)) {
      newestByProjectId.set(projectId, { projectId, viewedAt: entry.viewedAt });
    }
  }

  const normalized = Array.from(newestByProjectId.values())
    .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt))
    .filter((entry) => now - new Date(entry.viewedAt).getTime() <= MAX_AGE_MS)
    .slice(0, MAX_ENTRIES);

  writeEntries(normalized);

  return normalized.map((entry) => entry.projectId);
}

/**
 * Removes any cached entry whose projectId is not present in foundProjectIds, then persists.
 * @param {string[]} foundProjectIds
 */
export function pruneMissingProjectIds(foundProjectIds) {
  const keep = new Set(foundProjectIds);
  writeEntries(readEntries().filter((entry) => keep.has(entry.projectId)));
}
